import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';

/**
 * Editable number field with increment/decrement support
 * 
 * Features:
 * - Click to edit inline
 * - Arrow key increment/decrement
 * - Configurable precision and bounds
 * - Escape to cancel, Enter to confirm
 * - Accessibility support with aria labels
 * 
 * @example
 * <EditableNumber
 *   value={10.50}
 *   onChange={(val) => setPrice(val)}
 *   prefix="$"
 *   precision={2}
 *   min={0}
 * />
 */
function EditableNumber({
  value = 0,
  onChange,
  placeholder = '0.00',
  error,
  disabled = false,
  min = 0,
  max,
  step = 0.01,
  precision = 2,
  prefix = '',
  className = '',
  onBlur,
  ariaLabel = 'Editable number field',
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value?.toString() || '');
  const inputRef = useRef(null);

  // Sync local value with prop changes
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value?.toString() || '');
    }
  }, [value, isEditing]);

  // Auto-focus and select on edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Parse and clamp value within bounds
  const parseValue = useCallback((val) => {
    const num = parseFloat(val) || 0;
    const clamped = Math.max(min, max !== undefined ? Math.min(num, max) : num);
    return clamped;
  }, [min, max]);

  // Format value with specified precision
  const formatValue = useCallback((val) => {
    return parseValue(val).toFixed(precision);
  }, [parseValue, precision]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange?.(parseFloat(newValue) || 0);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    const formatted = formatValue(localValue);
    setLocalValue(formatted);
    setIsEditing(false);
    onBlur?.(parseFloat(formatted));
  }, [formatValue, localValue, onBlur]);

  const handleClick = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
    }
  }, [disabled]);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        setLocalValue(value?.toString() || '');
        setIsEditing(false);
        break;
      case 'Enter':
        handleBlur();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const incrementedVal = parseValue(parseFloat(localValue) + step);
        setLocalValue(incrementedVal.toString());
        onChange?.(incrementedVal);
        break;
      case 'ArrowDown':
        e.preventDefault();
        const decrementedVal = parseValue(parseFloat(localValue) - step);
        setLocalValue(decrementedVal.toString());
        onChange?.(decrementedVal);
        break;
      default:
        break;
    }
  }, [value, handleBlur, parseValue, localValue, step, onChange]);

  if (!isEditing) {
    return (
      <div
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        className={`
          p-2 rounded cursor-pointer transition-all
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300'}
          ${error ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-300'}
          ${className}
        `}
      >
        <div className="text-sm text-gray-900 font-mono">
          {prefix}
          {formatValue(localValue)}
        </div>
        {error && <div className="text-xs text-red-600 mt-1" role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="number"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      aria-label={ariaLabel}
      aria-invalid={!!error}
      className={`
        w-full px-2 py-2 text-sm rounded border-2 border-blue-500
        focus:outline-none focus:ring-2 focus:ring-blue-200
        ${error ? 'border-red-500 bg-red-50' : 'bg-white'}
      `}
    />
  );
}

EditableNumber.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  precision: PropTypes.number,
  prefix: PropTypes.string,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default memo(EditableNumber);
